import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import RoleFormModal, { colorCfg, resolveRoleColor } from './components/RoleFormModal';
import ConfirmModal from '../../components/base/ConfirmModal';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  createPlatformRole,
  deletePlatformRole,
  fetchPlatformRoles,
  updatePlatformRole,
  type PlatformRole,
} from '../../api/platformRoles.api';
import { ALL_PLATFORM_PERMISSIONS, PLATFORM_PERMISSION_GROUPS } from '../../config/platform-permissions';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';

export default function AdminRolesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formModal, setFormModal] = useState<{ open: boolean; role: PlatformRole | null }>({ open: false, role: null });
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const r = await fetchPlatformRoles();
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      return;
    }
    setRoles(r.roles);
    setSelectedId((prev) => prev ?? r.roles[0]?.id ?? null);
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = roles.find((r) => r.id === selectedId) ?? roles[0];

  const handleSave = async (data: {
    name: string;
    description: string;
    color: string;
    permissions: string[];
  }) => {
    setBusy(true);
    setError('');
    if (formModal.role) {
      const r = await updatePlatformRole(formModal.role.id, data);
      setBusy(false);
      if (r.ok === false) {
        setError(r.message);
        return;
      }
    } else {
      const r = await createPlatformRole(data);
      setBusy(false);
      if (r.ok === false) {
        setError(r.message);
        return;
      }
      setSelectedId(r.role.id);
    }
    setFormModal({ open: false, role: null });
    await load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setBusy(true);
    const r = await deletePlatformRole(deleteId);
    setBusy(false);
    setDeleteId(null);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    setSelectedId(null);
    await load();
  };

  const cc = selected ? colorCfg[resolveRoleColor(selected.color)] : colorCfg.teal;

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        ) : null}

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Total Roles', value: roles.length, icon: 'ri-shield-user-line', bg: 'bg-teal-50 text-teal-600' },
            { label: 'System Roles', value: roles.filter((r) => r.is_system).length, icon: 'ri-lock-line', bg: 'bg-amber-50 text-amber-600' },
            { label: 'Custom Roles', value: roles.filter((r) => !r.is_system).length, icon: 'ri-user-settings-line', bg: 'bg-emerald-50 text-emerald-600' },
            { label: 'Total Perms', value: ALL_PLATFORM_PERMISSIONS.length, icon: 'ri-key-2-line', bg: 'bg-rose-50 text-rose-500' },
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-xl p-4 ring-1 ring-gray-100 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.bg}`}>
                <i className={`${c.icon} text-lg`} />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">{c.label}</p>
                <p className="text-xl font-bold text-gray-900">{loading ? '…' : c.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-5 items-start flex-col xl:flex-row">
          <div className="w-full xl:w-72 flex-shrink-0 flex flex-col gap-3">
            {can('add_admin_role') ? (
              <button
                type="button"
                onClick={() => setFormModal({ open: true, role: null })}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-teal-300 text-teal-600 text-sm font-semibold hover:bg-teal-50"
              >
                <i className="ri-add-line" />
                {t('admin_roles.add_role')}
              </button>
            ) : null}

            {roles.map((role) => {
              const c = colorCfg[resolveRoleColor(role.color)];
              const active = selected?.id === role.id;
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedId(role.id)}
                  className={`text-start rounded-xl p-4 ring-1 transition-all ${
                    active ? 'ring-teal-400 bg-teal-50/40' : 'ring-gray-100 bg-white hover:ring-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                    <p className="text-sm font-bold text-gray-900">{role.name}</p>
                    {role.is_system ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{t('admin_roles.system_role')}</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-2">{role.description}</p>
                  <p className="text-[11px] text-gray-400 mt-2">
                    {role.permissions?.length ?? 0} {t('admin_roles.permissions_count')} · {role.users_count ?? 0} {t('admin_roles.users_using')}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="flex-1 bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden w-full">
            {!selected ? (
              <p className="px-5 py-10 text-center text-sm text-gray-400">{t('admin_roles.no_roles')}</p>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${cc.dot}`} />
                      <h3 className="text-base font-bold text-gray-900">{selected.name}</h3>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{selected.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {can('edit_admin_role') ? (
                      <button
                        type="button"
                        onClick={() => setFormModal({ open: true, role: selected })}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      >
                        تعديل
                      </button>
                    ) : null}
                    {can('delete_admin_role') && !selected.is_system ? (
                      <button
                        type="button"
                        onClick={() => setDeleteId(selected.id)}
                        className="px-3 py-2 rounded-lg border border-rose-200 text-rose-600 text-sm"
                      >
                        حذف
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PLATFORM_PERMISSION_GROUPS.map((group) => {
                    const enabled = group.perms.filter((p) => selected.permissions?.includes(p)).length;
                    const locked = enabled === 0;
                    return (
                      <div
                        key={group.key}
                        className={`rounded-xl border p-4 ${locked ? 'border-gray-100 opacity-60' : 'border-teal-100 bg-teal-50/30'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <i className={`${group.icon} text-teal-600`} />
                            <p className="text-sm font-bold text-gray-800">
                              {t(`admin_roles.groups.${group.key}`, { defaultValue: group.key })}
                            </p>
                          </div>
                          <span className="text-[11px] text-gray-500">
                            {enabled}/{group.perms.length}
                            {locked ? ' · مقفل' : ''}
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {group.perms.map((perm) => (
                            <li key={perm} className="text-xs flex items-center gap-2">
                              <i className={`ri-${selected.permissions?.includes(perm) ? 'checkbox-circle-fill text-teal-600' : 'checkbox-blank-circle-line text-gray-300'}`} />
                              <span className={selected.permissions?.includes(perm) ? 'text-gray-800' : 'text-gray-400'}>
                                {t(`admin_roles.perms.${perm}`, { defaultValue: perm })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <RoleFormModal
        isOpen={formModal.open}
        role={formModal.role}
        saving={busy}
        onClose={() => setFormModal({ open: false, role: null })}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={deleteId !== null}
        title={t('admin_roles.confirm_delete')}
        message={t('admin_roles.confirm_delete')}
        confirmVariant="danger"
        onConfirm={() => void handleDelete()}
        onClose={() => setDeleteId(null)}
      />
    </AdminLayout>
  );
}
